import { useState } from "react";
import {
  ChevronDown,
  Copy,
  GripVertical,
  Lock,
  LockOpen,
  Plus,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { formatMoney } from "@/lib/format";

const LABELS = {
  cover: "Cover Page",
  heading: "Heading",
  text: "Rich Text",
  bullets: "Bullet List",
  keyvalue: "Key-Value Rows",
  pricing: "Pricing Table",
  tiered_pricing: "Tiered Pricing",
  timeline: "Timeline",
  case_study: "Case Study",
  team_member: "Team Member",
  terms: "Terms & Conditions",
  signature: "Signature",
  image: "Image",
  page_break: "Page Break",
  spacer: "Spacer",
};

const field = "bg-zinc-900/70 border-zinc-800 text-sm text-zinc-100 focus-visible:ring-indigo-500/40";

const AI_ACTIONS = [
  ["rewrite", "Rewrite"],
  ["shorten", "Shorten"],
  ["expand", "Expand"],
  ["formal", "Formal"],
  ["direct", "Direct"],
  ["translate", "Translate"],
];

function StringList({ values = [], onChange, placeholder, testid }) {
  return (
    <div className="space-y-2">
      {values.map((v, i) => (
        <div key={i} className="flex gap-2">
          <Input
            data-testid={`${testid}-item-${i}`}
            className={field}
            value={v}
            placeholder={placeholder}
            onChange={(e) => onChange(values.map((x, j) => (j === i ? e.target.value : x)))}
          />
          <Button variant="ghost" size="icon" data-testid={`${testid}-remove-${i}`} onClick={() => onChange(values.filter((_, j) => j !== i))}>
            <X className="w-4 h-4 text-zinc-500" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" data-testid={`${testid}-add`} className="border-zinc-800 text-zinc-300" onClick={() => onChange([...values, ""])}>
        <Plus className="w-3.5 h-3.5 mr-1" /> Add item
      </Button>
    </div>
  );
}

function BlockFields({ block, patch, currency }) {
  const d = block.data || {};
  const set = (k, v) => patch({ ...d, [k]: v });
  const t = block.type;

  if (t === "cover")
    return (
      <div className="space-y-2">
        <Input data-testid="cover-title-input" className={field} value={d.title || ""} placeholder="Title" onChange={(e) => set("title", e.target.value)} />
        <Input data-testid="cover-subtitle-input" className={field} value={d.subtitle || ""} placeholder="Subtitle" onChange={(e) => set("subtitle", e.target.value)} />
        <Input data-testid="cover-preparedby-input" className={field} value={d.prepared_by || ""} placeholder="Prepared by" onChange={(e) => set("prepared_by", e.target.value)} />
        <select data-testid="cover-style-select" className={`w-full h-9 rounded-md px-2 ${field}`} value={d.style || "editorial"} onChange={(e) => set("style", e.target.value)}>
          {["editorial", "bold", "minimal", "classic"].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
    );

  if (t === "heading")
    return (
      <div className="flex gap-2">
        <Input data-testid="heading-text-input" className={field} value={d.text || ""} onChange={(e) => set("text", e.target.value)} />
        <select data-testid="heading-level-select" className={`h-9 rounded-md px-2 ${field}`} value={d.level || 2} onChange={(e) => set("level", Number(e.target.value))}>
          {[1, 2, 3, 4].map((l) => (
            <option key={l} value={l}>H{l}</option>
          ))}
        </select>
      </div>
    );

  if (t === "text")
    return (
      <Textarea
        data-testid="text-html-input"
        className={`${field} min-h-[120px] font-mono text-xs`}
        value={d.html || ""}
        onChange={(e) => set("html", e.target.value)}
        placeholder="<p>Your copy here</p>"
      />
    );

  if (t === "bullets") return <StringList testid="bullets" values={d.items || []} onChange={(v) => set("items", v)} placeholder="Bullet point" />;

  if (t === "keyvalue")
    return (
      <div className="space-y-2">
        {(d.rows || []).map((r, i) => (
          <div key={i} className="flex gap-2">
            <Input data-testid={`kv-key-${i}`} className={field} value={r.key} placeholder="Label" onChange={(e) => set("rows", d.rows.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))} />
            <Input data-testid={`kv-value-${i}`} className={field} value={r.value} placeholder="Value" onChange={(e) => set("rows", d.rows.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
            <Button variant="ghost" size="icon" onClick={() => set("rows", d.rows.filter((_, j) => j !== i))}>
              <X className="w-4 h-4 text-zinc-500" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" data-testid="kv-add-row" className="border-zinc-800 text-zinc-300" onClick={() => set("rows", [...(d.rows || []), { key: "", value: "" }])}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add row
        </Button>
      </div>
    );

  if (t === "pricing")
    return (
      <div className="space-y-3">
        <div className="flex gap-2">
          <Input data-testid="pricing-title-input" className={field} value={d.title || ""} placeholder="Section title" onChange={(e) => set("title", e.target.value)} />
          <select data-testid="pricing-gst-select" className={`h-9 rounded-md px-2 ${field}`} value={d.gst_mode || "none"} onChange={(e) => set("gst_mode", e.target.value)}>
            <option value="none">No tax split</option>
            <option value="cgst_sgst">CGST + SGST</option>
            <option value="igst">IGST</option>
            <option value="single">Single GST</option>
          </select>
        </div>
        {(d.lines || []).map((l, i) => (
          <div key={l.id || i} className="rounded-lg border border-zinc-800 p-3 space-y-2 bg-zinc-900/40">
            <div className="flex gap-2">
              <Input data-testid={`pricing-line-name-${i}`} className={field} value={l.name} placeholder="Line item" onChange={(e) => set("lines", d.lines.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
              <Button variant="ghost" size="icon" data-testid={`pricing-line-remove-${i}`} onClick={() => set("lines", d.lines.filter((_, j) => j !== i))}>
                <X className="w-4 h-4 text-zinc-500" />
              </Button>
            </div>
            <Input className={field} value={l.description || ""} placeholder="Description (optional)" onChange={(e) => set("lines", d.lines.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} />
            <div className="grid grid-cols-4 gap-2">
              {[
                ["qty", "Qty", 1],
                ["unit_price_minor", "Rate (minor)", 100],
                ["discount_pct", "Disc %", 1],
                ["tax_pct", "Tax %", 1],
              ].map(([key, label]) => (
                <div key={key}>
                  <label className="text-[10px] uppercase tracking-wide text-zinc-500 font-num">{label}</label>
                  <Input
                    data-testid={`pricing-line-${key}-${i}`}
                    type="number"
                    className={`${field} font-num`}
                    value={l[key] ?? 0}
                    onChange={(e) => set("lines", d.lines.map((x, j) => (j === i ? { ...x, [key]: Number(e.target.value) } : x)))}
                  />
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-500 font-num">Rate shown: {formatMoney(l.unit_price_minor, currency)}</p>
          </div>
        ))}
        <Button
          variant="outline"
          size="sm"
          data-testid="pricing-add-line"
          className="border-zinc-800 text-zinc-300"
          onClick={() => set("lines", [...(d.lines || []), { id: `l${Date.now()}`, name: "", description: "", qty: 1, unit_price_minor: 0, discount_pct: 0, tax_pct: 18 }])}
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add line item
        </Button>
        <Input className={field} value={d.note || ""} placeholder="Note under the table" onChange={(e) => set("note", e.target.value)} />
      </div>
    );

  if (t === "tiered_pricing")
    return (
      <div className="space-y-3">
        {(d.tiers || []).map((tier, i) => (
          <div key={tier.id || i} className="rounded-lg border border-zinc-800 p-3 space-y-2 bg-zinc-900/40">
            <div className="flex gap-2">
              <Input data-testid={`tier-name-${i}`} className={field} value={tier.name} placeholder="Tier name" onChange={(e) => set("tiers", d.tiers.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
              <Input data-testid={`tier-price-${i}`} type="number" className={`${field} font-num`} value={tier.price_minor} onChange={(e) => set("tiers", d.tiers.map((x, j) => (j === i ? { ...x, price_minor: Number(e.target.value) } : x)))} />
            </div>
            <div className="flex items-center gap-3">
              <Input className={field} value={tier.cadence || ""} placeholder="one-time / monthly" onChange={(e) => set("tiers", d.tiers.map((x, j) => (j === i ? { ...x, cadence: e.target.value } : x)))} />
              <label className="flex items-center gap-1.5 text-xs text-zinc-400 whitespace-nowrap">
                <input data-testid={`tier-highlight-${i}`} type="checkbox" checked={!!tier.highlight} onChange={(e) => set("tiers", d.tiers.map((x, j) => ({ ...x, highlight: j === i ? e.target.checked : false })))} />
                Recommended
              </label>
            </div>
            <StringList testid={`tier-${i}-features`} values={tier.features || []} onChange={(v) => set("tiers", d.tiers.map((x, j) => (j === i ? { ...x, features: v } : x)))} placeholder="Feature" />
          </div>
        ))}
      </div>
    );

  if (t === "timeline")
    return (
      <div className="space-y-2">
        {(d.steps || []).map((s, i) => (
          <div key={i} className="rounded-lg border border-zinc-800 p-3 space-y-2 bg-zinc-900/40">
            <div className="flex gap-2">
              <Input data-testid={`timeline-label-${i}`} className={field} value={s.label} placeholder="Phase" onChange={(e) => set("steps", d.steps.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
              <Input data-testid={`timeline-duration-${i}`} className={field} value={s.duration} placeholder="Week 1" onChange={(e) => set("steps", d.steps.map((x, j) => (j === i ? { ...x, duration: e.target.value } : x)))} />
              <Button variant="ghost" size="icon" onClick={() => set("steps", d.steps.filter((_, j) => j !== i))}>
                <X className="w-4 h-4 text-zinc-500" />
              </Button>
            </div>
            <Input className={field} value={s.detail || ""} placeholder="Detail" onChange={(e) => set("steps", d.steps.map((x, j) => (j === i ? { ...x, detail: e.target.value } : x)))} />
          </div>
        ))}
        <Button variant="outline" size="sm" data-testid="timeline-add-step" className="border-zinc-800 text-zinc-300" onClick={() => set("steps", [...(d.steps || []), { label: "", duration: "", detail: "" }])}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add phase
        </Button>
      </div>
    );

  if (t === "case_study")
    return (
      <div className="space-y-2">
        <Input data-testid="case-client-input" className={field} value={d.client || ""} placeholder="Client" onChange={(e) => set("client", e.target.value)} />
        <Input data-testid="case-result-input" className={field} value={d.result || ""} placeholder="Headline result" onChange={(e) => set("result", e.target.value)} />
        <Textarea className={field} value={d.body || ""} placeholder="What we did" onChange={(e) => set("body", e.target.value)} />
        <Input className={field} value={d.image_url || ""} placeholder="Image URL" onChange={(e) => set("image_url", e.target.value)} />
      </div>
    );

  if (t === "team_member")
    return (
      <div className="space-y-2">
        <Input data-testid="team-name-input" className={field} value={d.name || ""} placeholder="Name" onChange={(e) => set("name", e.target.value)} />
        <Input data-testid="team-role-input" className={field} value={d.role || ""} placeholder="Role" onChange={(e) => set("role", e.target.value)} />
        <Textarea className={field} value={d.bio || ""} placeholder="Short bio" onChange={(e) => set("bio", e.target.value)} />
        <Input className={field} value={d.image_url || ""} placeholder="Photo URL" onChange={(e) => set("image_url", e.target.value)} />
      </div>
    );

  if (t === "terms")
    return (
      <div className="space-y-2">
        {(d.clauses || []).map((c, i) => (
          <div key={i} className="rounded-lg border border-zinc-800 p-3 space-y-2 bg-zinc-900/40">
            <div className="flex gap-2">
              <Input data-testid={`terms-title-${i}`} className={field} value={c.title} placeholder="Clause title" onChange={(e) => set("clauses", d.clauses.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
              <Button variant="ghost" size="icon" onClick={() => set("clauses", d.clauses.filter((_, j) => j !== i))}>
                <X className="w-4 h-4 text-zinc-500" />
              </Button>
            </div>
            <Textarea className={field} value={c.body} placeholder="Clause body" onChange={(e) => set("clauses", d.clauses.map((x, j) => (j === i ? { ...x, body: e.target.value } : x)))} />
          </div>
        ))}
        <Button variant="outline" size="sm" data-testid="terms-add-clause" className="border-zinc-800 text-zinc-300" onClick={() => set("clauses", [...(d.clauses || []), { title: "", body: "" }])}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add clause
        </Button>
      </div>
    );

  if (t === "signature")
    return (
      <div className="space-y-2">
        <Textarea data-testid="signature-prompt-input" className={field} value={d.prompt || ""} onChange={(e) => set("prompt", e.target.value)} />
        <Input data-testid="signature-role-input" className={field} value={d.signer_role || ""} placeholder="Signer role" onChange={(e) => set("signer_role", e.target.value)} />
      </div>
    );

  if (t === "image")
    return (
      <div className="space-y-2">
        <Input data-testid="image-url-input" className={field} value={d.url || ""} placeholder="https://..." onChange={(e) => set("url", e.target.value)} />
        <Input className={field} value={d.caption || ""} placeholder="Caption" onChange={(e) => set("caption", e.target.value)} />
        <select className={`w-full h-9 rounded-md px-2 ${field}`} value={d.width || "full"} onChange={(e) => set("width", e.target.value)}>
          <option value="full">Full width</option>
          <option value="half">Half width</option>
        </select>
      </div>
    );

  if (t === "spacer")
    return (
      <Input data-testid="spacer-height-input" type="number" className={`${field} font-num`} value={d.height || 32} onChange={(e) => set("height", Number(e.target.value))} />
    );

  return <p className="text-xs text-zinc-500">No settings for this block.</p>;
}

export const BlockCard = ({
  block,
  index,
  currency,
  isAdmin,
  onPatch,
  onDelete,
  onDuplicate,
  onToggleLock,
  onDragStart,
  onDragOver,
  onDrop,
  onAI,
  aiBusy,
  dragOver,
  flagged,
}) => {
  const [open, setOpen] = useState(false);
  const locked = block.locked && !isAdmin;
  const aiCapable = ["text", "heading", "bullets", "terms", "case_study", "team_member"].includes(block.type);

  return (
    <div
      draggable={!locked}
      onDragStart={() => onDragStart(index)}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver(index);
      }}
      onDrop={() => onDrop(index)}
      data-testid={`block-card-${index}`}
      className={`nv-card p-3 ${dragOver ? "nv-drag-over" : ""} ${flagged ? "border-amber-500/40" : ""}`}
    >
      <div className="flex items-center gap-2">
        <GripVertical className={`w-4 h-4 shrink-0 ${locked ? "text-zinc-700" : "text-zinc-600 cursor-grab"}`} data-testid={`block-drag-handle-${index}`} />
        <button data-testid={`block-toggle-${index}`} onClick={() => setOpen((o) => !o)} className="flex-1 flex items-center gap-2 text-left min-w-0">
          <span className="text-[10px] font-num text-zinc-600 w-5">{String(index + 1).padStart(2, "0")}</span>
          <span className="text-sm text-zinc-200 truncate">{LABELS[block.type] || block.type}</span>
          {block.locked && <Lock className="w-3 h-3 text-amber-400 shrink-0" />}
          {flagged && <span className="text-[10px] text-amber-400 font-num shrink-0">FLAGGED</span>}
          <ChevronDown className={`w-4 h-4 text-zinc-600 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {isAdmin && (
          <button data-testid={`block-lock-${index}`} onClick={() => onToggleLock(block)} className="p-1.5 rounded hover:bg-zinc-800 transition-colors" title="Freeze block">
            {block.locked ? <Lock className="w-3.5 h-3.5 text-amber-400" /> : <LockOpen className="w-3.5 h-3.5 text-zinc-500" />}
          </button>
        )}
        <button data-testid={`block-duplicate-${index}`} onClick={() => onDuplicate(index)} className="p-1.5 rounded hover:bg-zinc-800 transition-colors">
          <Copy className="w-3.5 h-3.5 text-zinc-500" />
        </button>
        <button data-testid={`block-delete-${index}`} onClick={() => onDelete(index)} disabled={locked} className="p-1.5 rounded hover:bg-red-500/15 transition-colors disabled:opacity-30">
          <Trash2 className="w-3.5 h-3.5 text-zinc-500" />
        </button>
      </div>

      {open && (
        <div className="mt-3 pt-3 border-t border-zinc-800/70 space-y-3 nv-rise">
          {locked ? (
            <p className="text-xs text-amber-400/80">This block is frozen by an admin.</p>
          ) : (
            <>
              <BlockFields block={block} currency={currency} patch={(data) => onPatch(index, { ...block, data })} />
              {aiCapable && (
                <div className="flex flex-wrap gap-1.5 pt-2 border-t border-zinc-800/60">
                  <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-indigo-400/80 font-num mr-1">
                    <Sparkles className="w-3 h-3" /> AI
                  </span>
                  {AI_ACTIONS.map(([key, label]) => (
                    <button
                      key={key}
                      data-testid={`floating-ai-${key}-btn`}
                      disabled={aiBusy}
                      onClick={() => onAI(index, key)}
                      className="px-2 py-1 rounded-md text-[11px] border border-indigo-500/25 text-indigo-300 hover:bg-indigo-500/15 transition-colors disabled:opacity-40"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BlockCard;
